import * as React from 'react';
import {
    Box, Typography, Paper, Chip, Divider, List, ListItem, ListItemIcon,
    ListItemText, Tabs, Tab, Stack, Accordion, AccordionSummary, AccordionDetails,
    Alert, AlertTitle,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NewReleasesIcon from '@mui/icons-material/NewReleases';

import { funcionalidadesAtuais, versoes } from '../data/changelog';

const tipoConfig = {
    novo: { label: 'Novo', color: 'success', icon: <AutoAwesomeIcon fontSize="small" /> },
    melhoria: { label: 'Melhoria', color: 'info', icon: <TuneIcon fontSize="small" /> },
    fix: { label: 'Correcao', color: 'warning', icon: <BuildCircleIcon fontSize="small" /> },
};

function TabPanel({ value, index, children }) {
    return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

function FuncionalidadesView() {
    return (
        <Stack spacing={2}>
            {funcionalidadesAtuais.map((grupo) => (
                <Paper key={grupo.categoria} variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                        {grupo.categoria}
                    </Typography>
                    <Divider sx={{ mb: 1 }} />
                    <List dense disablePadding>
                        {grupo.itens.map((item) => (
                            <ListItem key={item} disableGutters>
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <CheckCircleOutlineIcon color="success" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary={item} />
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            ))}
        </Stack>
    );
}

function ChangelogView() {
    const formatarData = (iso) => {
        const [y, m, d] = iso.split('-');
        return `${d}/${m}/${y}`;
    };

    return (
        <Stack spacing={1.5}>
            {versoes.map((v, idx) => (
                <Accordion key={v.versao} defaultExpanded={idx === 0} disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            {idx === 0 && <NewReleasesIcon color="primary" />}
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Versao {v.versao}
                            </Typography>
                            <Chip size="small" label={formatarData(v.data)} variant="outlined" />
                            {idx === 0 && <Chip size="small" label="Mais recente" color="primary" />}
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <List dense disablePadding>
                            {v.mudancas.map((m, i) => {
                                const cfg = tipoConfig[m.tipo] || tipoConfig.novo;
                                return (
                                    <ListItem key={i} disableGutters alignItems="flex-start" sx={{ gap: 1.5 }}>
                                        <ListItemIcon sx={{ minWidth: 110, mt: 0.5 }}>
                                            <Chip
                                                size="small"
                                                color={cfg.color}
                                                icon={cfg.icon}
                                                label={cfg.label}
                                                sx={{ width: 100 }}
                                            />
                                        </ListItemIcon>
                                        <ListItemText primary={m.texto} sx={{ ml: 0 }} />
                                    </ListItem>
                                );
                            })}
                        </List>
                    </AccordionDetails>
                </Accordion>
            ))}
        </Stack>
    );
}

export default function Novidades() {
    const [tab, setTab] = React.useState(0);
    const ultimaVersao = versoes[0];
    const [versaoNova, setVersaoNova] = React.useState(false);

    React.useEffect(() => {
        if (!ultimaVersao) return;
        const ultimaVista = localStorage.getItem('sigapsi:ultimaVersaoVista');
        if (ultimaVista !== ultimaVersao.versao) {
            setVersaoNova(true);
            localStorage.setItem('sigapsi:ultimaVersaoVista', ultimaVersao.versao);
            window.dispatchEvent(new CustomEvent('sigapsi:novidadesVistas'));
            setTab(1);
        }
    }, [ultimaVersao]);

    return (
        <Box>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>Novidades</Typography>
                {ultimaVersao && (
                    <Chip
                        color="primary"
                        size="small"
                        icon={<NewReleasesIcon />}
                        label={`Versao atual: ${ultimaVersao.versao}`}
                    />
                )}
            </Box>

            {versaoNova && ultimaVersao && (
                <Alert severity="info" icon={<NewReleasesIcon />} sx={{ mb: 2 }} onClose={() => setVersaoNova(false)}>
                    <AlertTitle>Nova versao {ultimaVersao.versao} disponivel</AlertTitle>
                    {ultimaVersao.mudancas.length} mudanca(s) publicada(s) em {ultimaVersao.data.split('-').reverse().join('/')}.
                </Alert>
            )}

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tab label="Funcionalidades" />
                <Tab label="Atualizacoes" />
            </Tabs>

            <TabPanel value={tab} index={0}><FuncionalidadesView /></TabPanel>
            <TabPanel value={tab} index={1}><ChangelogView /></TabPanel>
        </Box>
    );
}
